import axiosInstance, {
  checkSession,
  logout as axiosLogout,
} from '@/utils/axiosInstance'
import { store } from '@/store/index'
import { verifyLogin } from '@/services/twofaService'
import {
  beginAuthentication,
  finishAuthentication,
  getAssertion,
} from '@/services/webauthnService'

export default {
  namespaced: true,
  state: {
    loggined: false,
    address: '',
    erc20Address: '',
    erc721Address: '',
    erc1155Address: '',
    chainID: 1,
    contractList: [],
    contractAddress: '',
    // 역할 기반 권한 관리
    groups: [],
    roles: [],
    permissions: [],
    // 2FA 관련
    twoFARequired: false,
    pendingAddress: null,
    pendingMessage: null,
    pendingSignature: null,
    pendingGoogleCredential: null,
  },
  getters: {
    isLoggined: (state) => state.loggined,
    address: (state) => state.address,
    erc20Address: (state) => state.erc20Address,
    erc721Address: (state) => state.erc721Address,
    erc1155Address: (state) => state.erc1155Address,
    chainID: (state) => state.chainID,
    contractList: (state) => state.contractList,
    contractAddress: (state) => state.contractAddress,
    groups: (state) => state.groups,
    roles: (state) => state.roles,
    permissions: (state) => state.permissions,
    hasGroup: (state) => (groupName) => state.groups.includes(groupName),
    hasRole: (state) => (roleName) => state.roles.includes(roleName),
    hasPermission: (state) => (route) => state.permissions.includes(route),
    isAdmin: (state) =>
      state.roles.includes('SUPER_ADMIN') || state.roles.includes('ADMIN'),
    isSuperAdmin: (state) => state.roles.includes('SUPER_ADMIN'),
  },
  mutations: {
    setLoginStatus(state, loggined) {
      state.loggined = loggined
    },
    setAddress(state, address) {
      state.address = address
    },
    setContractList(state, contractList) {
      state.contractList = JSON.parse(JSON.stringify(contractList))
    },
    setErc20Address(state, erc20Address) {
      state.erc20Address = erc20Address
    },
    setErc721Address(state, erc721Address) {
      state.erc721Address = erc721Address
    },
    setErc1155Address(state, erc1155Address) {
      state.erc1155Address = erc1155Address
    },
    setChainID(state, chainID) {
      state.chainID = chainID
    },
    setContractAddress(state, contractAddress) {
      state.contractAddress = contractAddress
    },
    // 그룹/역할/권한 관련 mutations
    setGroups(state, groups) {
      state.groups = groups || []
    },
    setRoles(state, roles) {
      state.roles = roles || []
    },
    setPermissions(state, permissions) {
      state.permissions = permissions || []
    },
    clearGroupsRolesAndPermissions(state) {
      state.groups = []
      state.roles = []
      state.permissions = []
    },
    setTwoFARequired(
      state,
      { required, address, message, signature, googleCredential }
    ) {
      state.twoFARequired = required
      state.pendingAddress = address || null
      state.pendingMessage = message || null
      state.pendingSignature = signature || null
      state.pendingGoogleCredential = googleCredential || null
    },
    clearTwoFA(state) {
      state.twoFARequired = false
      state.pendingAddress = null
      state.pendingMessage = null
      state.pendingSignature = null
      state.pendingGoogleCredential = null
    },
  },
  actions: {
    /**
     * Login action - sends signature to server
     * Server will set HttpOnly cookie with session ID
     */
    async login(context, { address, message, signature, callback }) {
      try {
        const result = await axiosInstance.post('/signIn', {
          address,
          message,
          signature,
        })

        if (result.status === 200) {
          // Check if 2FA is required
          if (result.data.requires_2fa || result.data.requires2fa) {
            context.commit('setTwoFARequired', {
              required: true,
              address,
              message,
              signature,
            })
            if (callback) callback('2fa_required')
            return
          }

          context.commit('setAddress', address)
          context.commit('setLoginStatus', true)

          if (result.data.groups) {
            context.commit('setGroups', result.data.groups)
          }
          if (result.data.roles) {
            context.commit('setRoles', result.data.roles)
          }
          if (result.data.permissions) {
            context.commit('setPermissions', result.data.permissions)
          }

          if (callback) callback(true)
        }
      } catch (_err) {
        if (callback) callback(false)
        store.dispatch(
          'toast/error',
          'You are unable to log in. Please verify your account credentials.'
        )
      }
    },

    async googleLogin(context, { credential, callback }) {
      try {
        const result = await axiosInstance.post('/googleSignIn', {
          credential,
        })

        if (result.status === 200) {
          // Check if 2FA is required
          if (result.data.requires_2fa || result.data.requires2fa) {
            context.commit('setTwoFARequired', {
              required: true,
              googleCredential: credential,
            })
            if (callback) callback('2fa_required')
            return
          }

          // Extract address from response (server returns it via session)
          const sessionData = await checkSession()
          if (sessionData) {
            context.commit('setAddress', sessionData.address)
            context.commit('setLoginStatus', true)
            if (result.data.groups) {
              context.commit('setGroups', result.data.groups)
            }
            if (result.data.roles) {
              context.commit('setRoles', result.data.roles)
            }
            if (result.data.permissions) {
              context.commit('setPermissions', result.data.permissions)
            }
          }

          if (callback) callback(true)
        }
      } catch (_err) {
        if (callback) callback(false)
        store.dispatch(
          'toast/error',
          'Google login failed. Please verify your account is registered with this email.'
        )
      }
    },

    async verifyTwoFA(context, { totpCode, callback }) {
      try {
        // Google login 2FA flow
        if (context.state.pendingGoogleCredential) {
          const result = await axiosInstance.post('/googleSignIn', {
            credential: context.state.pendingGoogleCredential,
            totp_code: totpCode,
          })

          if (result.status === 200) {
            const sessionData = await checkSession()
            if (sessionData) {
              context.commit('setAddress', sessionData.address)
              context.commit('setLoginStatus', true)
            }
            context.commit('clearTwoFA')

            if (result.data.groups) {
              context.commit('setGroups', result.data.groups)
            }
            if (result.data.roles) {
              context.commit('setRoles', result.data.roles)
            }
            if (result.data.permissions) {
              context.commit('setPermissions', result.data.permissions)
            }

            if (callback) callback(true)
          }
          return
        }

        // MetaMask login 2FA flow
        const { data, error } = await verifyLogin({
          address: context.state.pendingAddress,
          message: context.state.pendingMessage,
          signature: context.state.pendingSignature,
          totp_code: totpCode,
        })

        if (error) {
          if (callback) callback(false)
          return
        }

        context.commit('setAddress', context.state.pendingAddress)
        context.commit('setLoginStatus', true)
        context.commit('clearTwoFA')

        if (data.groups) {
          context.commit('setGroups', data.groups)
        }
        if (data.roles) {
          context.commit('setRoles', data.roles)
        }
        if (data.permissions) {
          context.commit('setPermissions', data.permissions)
        }

        if (callback) callback(true)
      } catch (_err) {
        if (callback) callback(false)
        store.dispatch('toast/error', 'Invalid 2FA code. Please try again.')
      }
    },

    /**
     * WebAuthn authentication - use security key instead of TOTP code
     */
    async verifyWebAuthn(context, { callback }) {
      try {
        // Determine the user identifier for the authentication request
        const params = {}
        if (context.state.pendingAddress) {
          params.address = context.state.pendingAddress
        }
        if (context.state.pendingGoogleCredential) {
          params.google_credential = context.state.pendingGoogleCredential
        }

        // Step 1: Get authentication challenge from server
        const { data: options, error } = await beginAuthentication(params)
        if (error || !options) {
          if (callback) callback(false)
          return
        }

        // Step 2: Get assertion from the hardware security key via browser API
        const assertionResponse = await getAssertion(options)

        // Step 3: Send assertion to server for verification
        const payload = {
          ...assertionResponse,
          address: context.state.pendingAddress || undefined,
          message: context.state.pendingMessage || undefined,
          signature: context.state.pendingSignature || undefined,
          google_credential: context.state.pendingGoogleCredential || undefined,
        }
        const { data: authResult, error: finishError } =
          await finishAuthentication(payload)

        if (finishError || !authResult) {
          if (callback) callback(false)
          return
        }

        // Step 4: Complete login
        const sessionData = await checkSession()
        if (sessionData) {
          context.commit('setAddress', sessionData.address)
          context.commit('setLoginStatus', true)
          context.commit('setGroups', sessionData.groups)
          context.commit('setRoles', sessionData.roles)
          context.commit('setPermissions', sessionData.permissions)
        }
        context.commit('clearTwoFA')

        if (callback) callback(true)
      } catch (err) {
        if (callback) callback(false)
        if (err.name === 'NotAllowedError') {
          store.dispatch(
            'toast/error',
            'Security key authentication was cancelled.'
          )
        } else {
          store.dispatch(
            'toast/error',
            err.message || 'Security key authentication failed.'
          )
        }
      }
    },

    /**
     * Logout action - clears session on server
     */
    async logout(context) {
      await axiosLogout()

      context.commit('setAddress', '')
      context.commit('setContractList', [])
      context.commit('setErc20Address', '')
      context.commit('setErc721Address', '')
      context.commit('setErc1155Address', '')
      context.commit('setChainID', 0)
      context.commit('setContractAddress', '')
      context.commit('setLoginStatus', false)
      context.commit('clearGroupsRolesAndPermissions')
    },

    /**
     * Check login status from server session
     */
    async checkLoginStatus(context) {
      const sessionData = await checkSession()

      if (sessionData) {
        context.commit('setLoginStatus', true)
        context.commit('setAddress', sessionData.address)
        context.commit('setGroups', sessionData.groups)
        context.commit('setRoles', sessionData.roles)
        context.commit('setPermissions', sessionData.permissions)

        // Load contract list
        try {
          const r = await axiosInstance.post('/contract/list', {
            address: sessionData.address,
            offset: 0,
            limit: 1000,
          })

          if (r.status === 200) {
            const list = r.data.list
            const contractList = r.data.contractList.filter((item) =>
              list.includes(item.contractAddress)
            )

            context.commit('setContractList', contractList)
            const erc20List = contractList.filter(
              (item) => item.tokenType === 'erc20'
            )
            const erc721List = contractList.filter(
              (item) => item.tokenType === 'erc721'
            )
            const erc1155List = contractList.filter(
              (item) => item.tokenType === 'erc1155'
            )
            if (erc20List.length > 0) {
              context.commit('setErc20Address', erc20List[0].contractAddress)
              context.commit('setChainID', erc20List[0].chainID)
              context.commit('setContractAddress', erc20List[0].contractAddress)
            }
            if (erc721List.length > 0) {
              context.commit('setErc721Address', erc721List[0].contractAddress)
            }
            if (erc1155List.length > 0) {
              context.commit(
                'setErc1155Address',
                erc1155List[0].contractAddress
              )
            }
          }
        } catch (_) {
          /* session restore failure handled by else branch */
        }
      } else {
        context.commit('setLoginStatus', false)
        context.commit('setAddress', '')
        context.commit('setContractList', [])
        context.commit('setErc20Address', '')
        context.commit('setErc721Address', '')
        context.commit('setErc1155Address', '')
        context.commit('setChainID', 0)
        context.commit('setContractAddress', '')
        context.commit('clearGroupsRolesAndPermissions')
      }
    },

    setContractList(context, contractList) {
      context.commit('setContractList', contractList)
    },
    setErc20Address(context, erc20address) {
      context.commit('setErc20Address', erc20address)
    },
    setErc721Address(context, erc721address) {
      context.commit('setErc721Address', erc721address)
    },
    setErc1155Address(context, erc1155address) {
      context.commit('setErc1155Address', erc1155address)
    },
    setChainID(context, chainID) {
      context.commit('setChainID', chainID)
    },
    setContractAddress(context, contractAddress) {
      context.commit('setContractAddress', contractAddress)
    },
  },
}
